import React, { useEffect, useRef, useState } from 'react';
import { FiCalendar, FiChevronDown, FiFilter, FiRefreshCw, FiSearch, FiX } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RANGE_OPTIONS } from './dateRange';
import { PMS_OPTIONS } from './derive';

const HOST_TYPE_OPTIONS = [
  { id: 'all', label: 'All hosts' },
  { id: 'new', label: 'New hosts' },
  { id: 'legacy', label: 'Legacy hosts' },
];

const PROPERTY_SOURCE_OPTIONS = [
  { id: 'all', label: 'All properties' },
  { id: 'new', label: 'From new hosts' },
  { id: 'legacy', label: 'From legacy hosts' },
];

const ORDER_STATUS_OPTIONS = [
  { id: 'all', label: 'All statuses' },
  { id: 'completed', label: 'Completed' },
  { id: 'paymentCompleted', label: 'Payment completed' },
  { id: 'picked', label: 'Picked' },
  { id: 'pending', label: 'Pending' },
];

function useClickAway(ref, onAway) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      onAway();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onAway, ref]);
}

function CompactSelect({ value, onChange, options, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickAway(ref, () => setOpen(false));
  const current = options.find((o) => o.id === value) || options[0];
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
      >
        <span className="text-zinc-400">{label}</span>
        <span className="text-zinc-900">{current.label}</span>
        <FiChevronDown className="h-3.5 w-3.5 text-zinc-400" />
      </button>
      {open ? (
        <div className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg">
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange(opt.id);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs hover:bg-zinc-50 ${
                opt.id === value ? 'bg-indigo-50/50 text-indigo-700' : 'text-zinc-700'
              }`}
            >
              {opt.label}
              {opt.id === value ? <span className="text-indigo-500">●</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function DashboardFilterBar({
  filters,
  onChange,
  onReset,
  onRefresh,
  refreshing,
  hostCount,
}) {
  const [searchInput, setSearchInput] = useState(filters.hostQuery);
  const [moreOpen, setMoreOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const moreRef = useRef(null);
  const customRef = useRef(null);
  useClickAway(moreRef, () => setMoreOpen(false));
  useClickAway(customRef, () => setCustomOpen(false));

  // Debounce search input → filters.hostQuery
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== filters.hostQuery) onChange({ hostQuery: searchInput });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const hasActiveFilters =
    filters.range !== '30d' ||
    filters.pms !== 'all' ||
    filters.hostType !== 'all' ||
    filters.propertySource !== 'all' ||
    filters.orderStatus !== 'all' ||
    Boolean(filters.hostQuery);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 shadow-sm">
      {/* Date range segmented control */}
      <div className="flex items-center rounded-lg bg-zinc-100 p-0.5">
        {RANGE_OPTIONS.map((opt) => {
          const active = filters.range === opt.id;
          const isCustom = opt.id === 'custom';
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange({ range: opt.id });
                if (isCustom) setCustomOpen(true);
              }}
              title={opt.longLabel}
              className={`h-8 rounded-md px-2.5 text-xs font-medium transition ${
                active ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {filters.range === 'custom' ? (
        <div className="relative" ref={customRef}>
          <button
            type="button"
            onClick={() => setCustomOpen((v) => !v)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <FiCalendar className="h-3.5 w-3.5 text-zinc-400" />
            {filters.customStart && filters.customEnd
              ? `${new Date(filters.customStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(filters.customEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
              : 'Pick range'}
          </button>
          {customOpen ? (
            <div className="absolute left-0 z-40 mt-1 rounded-lg border border-zinc-200 bg-white p-3 shadow-lg">
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-medium text-zinc-600">Start</label>
                <DatePicker
                  selected={filters.customStart}
                  onChange={(d) => onChange({ customStart: d })}
                  selectsStart
                  startDate={filters.customStart}
                  endDate={filters.customEnd}
                  className="h-8 w-44 rounded-md border border-zinc-200 px-2 text-xs"
                />
                <label className="text-[11px] font-medium text-zinc-600">End</label>
                <DatePicker
                  selected={filters.customEnd}
                  onChange={(d) => onChange({ customEnd: d })}
                  selectsEnd
                  startDate={filters.customStart}
                  endDate={filters.customEnd}
                  minDate={filters.customStart}
                  className="h-8 w-44 rounded-md border border-zinc-200 px-2 text-xs"
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <CompactSelect
        label="PMS"
        value={filters.pms}
        onChange={(v) => onChange({ pms: v })}
        options={PMS_OPTIONS}
      />

      <CompactSelect
        label="Type"
        value={filters.hostType}
        onChange={(v) => onChange({ hostType: v })}
        options={HOST_TYPE_OPTIONS}
      />

      <div className="relative min-w-[200px] flex-1">
        <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search host name, email, phone, company"
          className="h-9 w-full rounded-lg border border-zinc-200 bg-white pl-8 pr-3 text-xs text-zinc-900 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
        />
      </div>

      <div className="relative" ref={moreRef}>
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
        >
          <FiFilter className="h-3.5 w-3.5 text-zinc-400" />
          More
          <FiChevronDown className="h-3.5 w-3.5 text-zinc-400" />
        </button>
        {moreOpen ? (
          <div className="absolute right-0 z-30 mt-1 w-72 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg">
            <div className="space-y-3 p-3">
              <div>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Property source</p>
                <div className="flex flex-wrap gap-1">
                  {PROPERTY_SOURCE_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => onChange({ propertySource: opt.id })}
                      className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                        filters.propertySource === opt.id
                          ? 'bg-zinc-900 text-white'
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Order status</p>
                <div className="flex flex-wrap gap-1">
                  {ORDER_STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => onChange({ orderStatus: opt.id })}
                      className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                        filters.orderStatus === opt.id
                          ? 'bg-zinc-900 text-white'
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {hasActiveFilters ? (
        <button
          type="button"
          onClick={() => {
            setSearchInput('');
            onReset();
          }}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100"
        >
          <FiX className="h-3.5 w-3.5" />
          Reset
        </button>
      ) : null}

      <div className="ml-auto flex items-center gap-2">
        {Number.isFinite(hostCount) ? (
          <span className="text-[11px] text-zinc-500">
            <span className="font-semibold text-zinc-900">{hostCount.toLocaleString('en-US')}</span> hosts in view
          </span>
        ) : null}
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
        >
          <FiRefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}
